import {
  Component,
  ElementRef,
  Inject,
  Input,
  HostListener,
  OnInit,
  ViewChild,
  InjectionToken,
  OnChanges,
  AfterViewInit,
  EventEmitter,
  Output,
  Injector,
  ComponentFactoryResolver,
} from '@angular/core';
import * as esri from 'esri-leaflet';
import * as L from 'leaflet';

import { Asset } from '@digital-platform/shared/domain';
import { TrackerDetailPopupComponent } from './tracker-detail-popup/tracker-detail-popup.component';

const INIT_COORDS = new InjectionToken<{ lat: number; lng: number }>(
  'INIT_COORDS'
);
@Component({
  selector: 'digital-platform-esri-map',
  templateUrl: './esri-map.component.html',
  styleUrls: ['./esri-map.component.scss'],
  providers: [
    {
      provide: INIT_COORDS,
      useValue: { lat: 30.01877504, lng: -90.398564352 },
    },
  ],
})
export class EsriMapComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() public assetsView: Asset[];
  @Input() public initLat;
  @Input() public initLng;
  @Input() public zoomAsset: Asset;
  @Output() public editAssetFromMap = new EventEmitter<Asset>();
  public markers: any[] = [];
  private marker = null;

  public currentWidth: number;
  public currentHeight: number;

  protected baseLayer: L.Layer;
  protected map: L.Map;

  // The primary Map
  @ViewChild('primaryMap', { static: true }) protected mapDivRef: ElementRef;
  protected mapDiv: HTMLDivElement;

  constructor(
    @Inject(INIT_COORDS) protected _initCoords: { lat: number; lng: number },
    private injector: Injector,
    private resolver: ComponentFactoryResolver
  ) {
    this.baseLayer = null;
  }

  ngOnChanges(changes): void {
    if (
      changes.zoomAsset &&
      changes.zoomAsset.currentValue &&
      changes.zoomAsset.currentValue != changes.zoomAsset.previousValue
    ) {
      this.map.setView(
        [
          changes.zoomAsset.currentValue.marker.position.lat,
          changes.zoomAsset.currentValue.marker.position.lng,
        ],
        27
      );
      this.marker = this.markers.find(
        (x) => x.options.title == changes.zoomAsset.currentValue.id.toString()
      );
      this.mapDivRef.nativeElement.focus();
      this.marker.openPopup();
    } else if (
      changes.zoomAsset &&
      changes.zoomAsset.currentValue != changes.zoomAsset.previousValue &&
      this.marker
    ) {
      this.marker.closePopup();
      this.marker = null;
    }
  }

  public ngOnInit(): void {
    // Reference to DIV containing map is used in Leaflet initialization
    this.mapDiv = this.mapDivRef.nativeElement;
    this.__initializeMap();
    this.__renderMap();
  }

  edit(asset: Asset): void {
    this.editAssetFromMap.emit(asset);
  }

  public ngAfterViewInit(): void {
    this.map.invalidateSize();
  }
  //Basic map initialization //
  protected __initializeMap(): void {
    this.__updateMapSize();
  }

  //Render the map (establish center and base layer)//
  protected __renderMap(): void {
    // Create Leaflet Map in fixed DIV - zoom level is hardcoded for simplicity
    let initialLat = this._initCoords.lat;
    let initialLng = this._initCoords.lng;
    if (this.assetsView?.length > 0) {
      initialLat = this.assetsView[0].marker.position.lat;
      initialLng = this.assetsView[0].marker.position.lng;
    } else if (this.initLat && this.initLng) {
      initialLat = this.initLat;
      initialLng = this.initLng;
    }
    this.map = L.map(this.mapDiv).setView([initialLat, initialLng], 7);
    this.renderMarkersInMap();
    this.baseLayer = esri.basemapLayer('Topographic');
    this.map.addLayer(this.baseLayer);
  }

  private renderMarkersInMap() {
    const icon = L.icon({
      iconUrl: '/assets/marker-icon.png',
    });

    this.assetsView?.forEach((asset) => {
      const component = this.resolver
        .resolveComponentFactory(TrackerDetailPopupComponent)
        .create(this.injector);
      component.instance.marker = asset;
      component.instance.editEvent.subscribe((editAsset: Asset) => {
        this.edit(editAsset);
      });

      const marker = L.marker(
        [asset.marker.position.lat, asset.marker.position.lng],
        {
          icon: icon,
          title: asset.id.toString(),
        }
      )
        .addTo(this.map)
        .bindPopup(component.location.nativeElement);
      this.markers.push(marker);
      component.changeDetectorRef.detectChanges();
    });
    console.log(this.markers);
  }

  @HostListener('window:resize', ['$event'])
  protected __onResize(event): void {
    this.__updateMapSize();

    this.map.invalidateSize();
  }

  protected __updateMapSize(): void {
    // update width/height settings as you see fit
    this.currentWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    this.currentHeight =
      (window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight) - 200;
  }

  getCurrentOffset = (map: L.Map): { x: number; y: number } => {
    const layerCorner: L.Point = map.getPixelOrigin();
    const mapCorner: L.Point = map.getPixelBounds().min;

    let xVal = 0;
    let yVal = 0;

    if (!layerCorner.equals(mapCorner)) {
      xVal = layerCorner.x - mapCorner.x;
      yVal = layerCorner.y - mapCorner.y;
    }

    return { x: xVal, y: yVal };
  };
}
